import { Radio, Space, Typography } from 'antd';
import { space } from '@viq-admin/ui';
import { CRM_COPY } from './copy';
import { HelpPopover } from './HelpHint';
import type { MergePolicy } from './types';

export interface MergePolicyControlProps {
  /** The policy this run would commit under — `policyOverride ?? tenantPolicy`.
   *  Never undefined: a radio group with nothing selected reads as a bug. */
  value: MergePolicy;
  /** `!canManage`, or the tenant default not yet known — visible but inert
   *  without `manage:CrmIntegration`. */
  disabled: boolean;
  /**
   * The company's Salesforce settings forbid overwriting (spec 2026-09-28
   * §5.8; the preview's `overwriteAllowed: false`): show fill blanks, inert,
   * with a note saying why.
   */
  locked?: boolean;
  onChange: (policy: MergePolicy) => void;
}

/**
 * The per-run merge policy, at the top of Match (gh-31 §4.7): the one rule
 * that governs every "Already in your CRM" row below it, stated before those
 * rows rather than hidden in Send's Options. Each option carries its own
 * one-line explanation. Each radio's accessible name is the label sentence
 * alone (`aria-label`), so screen readers — and tests — get the sentence, not
 * the sentence plus its footnote. `Radio.Group` renders a bare `<div>` with
 * no group role, so the wrapping `role="radiogroup"` div — not `Radio.Group`
 * — carries the group's own accessible name.
 */
export function MergePolicyControl({ value, disabled, locked, onChange }: MergePolicyControlProps) {
  return (
    <div data-testid="crm-merge-policy" style={{ marginBottom: space.lg }}>
      <Space size={space.xs} align="center" style={{ marginBottom: space.xs }}>
        <Typography.Text strong>{CRM_COPY.policyGroupLabel}</Typography.Text>
        <HelpPopover {...CRM_COPY.help.matchPolicy} testId="crm-help-match-policy" />
      </Space>
      <div role="radiogroup" aria-label={CRM_COPY.policyGroupLabel}>
        <Radio.Group
          disabled={disabled || locked}
          value={locked ? 'fillBlanksOnly' : value}
          onChange={(e) => onChange(e.target.value as MergePolicy)}
        >
          <Space direction="vertical" size={space.sm}>
            <Radio value="fillBlanksOnly" aria-label={CRM_COPY.policyFillBlanks}>
              <Typography.Text strong>{CRM_COPY.policyFillBlanks}</Typography.Text>
              <Typography.Text type="secondary" style={{ display: 'block' }}>
                {CRM_COPY.policyFillBlanksDetail}
              </Typography.Text>
            </Radio>
            <Radio value="overwrite" aria-label={CRM_COPY.policyOverwrite}>
              <Typography.Text strong>{CRM_COPY.policyOverwrite}</Typography.Text>
              <Typography.Text type="secondary" style={{ display: 'block' }}>
                {CRM_COPY.policyOverwriteDetail}
              </Typography.Text>
            </Radio>
          </Space>
        </Radio.Group>
      </div>
      {locked && (
        <Typography.Text type="secondary" style={{ display: 'block', marginTop: space.xs }}>
          {CRM_COPY.policyLockedNote}
        </Typography.Text>
      )}
    </div>
  );
}
